import type { AbstractLeadFormProps, AbstractLeadFormState } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import { AbstractLeadForm } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import type { AbstractLeadFormFields, LeadRequest, ProductType } from "@component/assets/AbstractLeadForm/types";
import { logError } from "@utils/sentry-error";
import type { ReactNode } from "react";
import React from "react";
import * as Step from "./Steps";
import type { PensionGapLeadCalcRequest, PensionGapLeadCalcResponse } from "./types";

/**
 * @Shortcode
 *
 * @title "Nyugdíjkalkulátor"
 * @shortcode "pension_gap_lead_form"
 * @slug "pension-gap"
 * @type "leadform"
 */
export default class PensionGapLeadForm<
	TRequest extends PensionGapLeadCalcRequest,
	TResponse extends PensionGapLeadCalcResponse,
	TFormFields extends AbstractLeadFormFields,
	TProps extends AbstractLeadFormProps<TRequest, TResponse>,
	TState extends AbstractLeadFormState<TFormFields>,
> extends AbstractLeadForm<TRequest, TResponse, TFormFields, TProps, TState> {
	// EventParams
	protected readonly productTypes: string = "SAVINGS_INSURANCE-PENSION_GAP";
	protected readonly componentType: string = "leadform";
	protected renderSteps(): ReactNode {
		const {
			values: { email },
			submitState,
		} = this.state;

		return (
			<>
				<Step.LeadForm
					stepName="lead-form"
					submitState={submitState}
					defaultTarget="success-page"
					title="Kérd részletes eredményeidet emailben!"
					button={<strong>Beküldöm</strong>}
					colorVersion="black"
					bottomLogo
					canStepBack
				/>
				<Step.SuccessPage
					stepName="success-page"
					subscriptionTypes={["RETAIL_NEWSLETTER"]}
					title="Köszönjük, hogy leadtad érdeklődésed!"
					userEmail={email}
					successMessage="Hamarosan elküldjük neked a kalkulációd eredményeit"
					colorVersion="black"
					bottomLogo
					subscriptionProductTypes={[this.getProductType()]}
				/>
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		if (target === "success-page") {
			this.sendLead()
				.then(() => {
					super.onStep(target, false);
				})
				.catch((error: Error) => {
					// TODO: Hibakezelés
					logError(error);
				});
		} else {
			super.onStep(target, withEvent);
		}
	}

	protected getLeadData(): LeadRequest<TRequest, TResponse> {
		const data = super.getLeadData();
		data.productSubType = "PENSION_GAP";

		return data;
	}

	protected getAutoresponderName(): string {
		return "autoresponder-pension-gap-calculator";
	}

	protected getAutoresponderFields(): Record<string, any> {
		const {
			values: { lastName, firstName },
		} = this.state;

		return {
			lastName,
			firstName,
		};
	}

	protected getProductType(): ProductType {
		return "SAVINGS_INSURANCE";
	}

	protected getComponentName(): string {
		return "PensionGapLeadForm";
	}
}
